import type { AssignedRiskNamesAndAddressesNameViewModel } from 'src/api/types/AssignedRiskNamesAndAddressesNameViewModel';
import type { AssignedRiskNamesAndAddressesAddressViewModel } from 'src/api/types/AssignedRiskNamesAndAddressesAddressViewModel';

import { useParams } from 'react-router';
import { useBoolean } from 'minimal-shared/hooks';

import { alpha } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Card,
  Chip,
  Alert,
  Table,
  Collapse,
  TableRow,
  Skeleton,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  Typography,
  CardContent,
  TableContainer,
} from '@mui/material';

import { MESSAGES } from 'src/constants/messages';
import { useAssignedRiskApplicationNamesAndAddresses } from 'src/api/hooks/useAssignedRiskApplicationQueries';

import { TableNoData } from 'src/components/table';

import { scrollbarStyles } from 'src/sections/employer-data/shared/styles/sharedStyles';

import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';

const namesMessages = MESSAGES.ASSIGNED_RISK_APPLICATION.NAMES_AND_ADDRESSES;

function formatAddress(address: AssignedRiskNamesAndAddressesAddressViewModel): string {
  const stateAndZip = [address.stateAbbreviation ?? address.stateName, address.zipCode]
    .filter(Boolean)
    .join(' ');
  const addressParts = [address.street, address.city, stateAndZip].filter(Boolean);

  return addressParts.join(', ');
}

function AddressTable({
  addresses,
  rowIndex,
}: Readonly<{
  addresses: AssignedRiskNamesAndAddressesAddressViewModel[];
  rowIndex: number;
}>) {
  return (
    <TableContainer
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <Table aria-label="assigned-risk-addresses">
        <TableHead>
          <TableRow>
            <TableCell>{namesMessages.ADDRESS_HEADER}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {addresses.map((address, addressIndex) => (
            <TableRow key={`${rowIndex}-${addressIndex}-${address.street ?? ''}`}>
              <TableCell data-testid={`assigned-risk-address-${rowIndex}-${addressIndex}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {formatAddress(address)}
                  {address.isPrimaryAddress ? (
                    <Chip
                      label={namesMessages.PRIMARY}
                      color="info"
                      variant="soft"
                      size="small"
                      data-testid={`assigned-risk-primary-address-chip-${rowIndex}-${addressIndex}`}
                    />
                  ) : null}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Row({ row, rowIndex }: Readonly<{ row: AssignedRiskNamesAndAddressesNameViewModel; rowIndex: number }>) {
  const open = useBoolean(true);
  const hasAddresses = Boolean(row.addresses && row.addresses.length > 0);

  return (
    <>
      <TableRow>
        <TableCell sx={{ width: 48 }}>
          {hasAddresses ? (
            <IconButton
              aria-label={`${open.value ? namesMessages.COLLAPSE_ADDRESSES : namesMessages.EXPAND_ADDRESSES} ${row.name ?? namesMessages.NAME_HEADER}`}
              aria-expanded={open.value}
              aria-controls={`assigned-risk-addresses-${rowIndex}`}
              size="small"
              onClick={open.onToggle}
            >
              {open.value ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell data-testid={`assigned-risk-name-${rowIndex}`}>
          <Typography component="span" sx={{ typography: 'body2', color: 'text.primary' }}>
            {row.name ?? ''}
          </Typography>
        </TableCell>
      </TableRow>
      {hasAddresses ? (
        <TableRow>
          <TableCell colSpan={2} sx={{ p: 0, border: 0 }}>
            <Collapse in={open.value} timeout="auto" unmountOnExit>
              <Box
                id={`assigned-risk-addresses-${rowIndex}`}
                sx={{
                  p: 1.5,
                  backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.4),
                }}
              >
                <AddressTable
                  addresses={row.addresses ?? []}
                  rowIndex={rowIndex}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

type NamesAndAddressesProps = {
  assignedRiskAppId?: number;
};

export default function NamesAndAddresses({ assignedRiskAppId: assignedRiskAppIdProp }: NamesAndAddressesProps = {}) {
  const params = useParams<{ assignedRiskAppId: string }>();
  const assignedRiskAppId = assignedRiskAppIdProp ?? Number(params.assignedRiskAppId);
  const headerWidth = useHeaderWidth();
  const { data, isLoading, isError } = useAssignedRiskApplicationNamesAndAddresses(assignedRiskAppId);
  const namesAndAddresses = data?.data?.names ?? [];

  return (
    <Card
      sx={[
        () => ({
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: 5,
          borderRadius: 2,
          boxShadow: 'var(--customShadows-card)',
          width: headerWidth,
        }),
      ]}
    >
      <CardContent sx={{ flexGrow: 1, padding: 0 }} data-testid="assigned-risk-names-addresses-card">
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="assigned-risk-names-addresses-title">
            {namesMessages.TITLE}
          </Typography>
          {isLoading ? (
            <Box data-testid="assigned-risk-names-addresses-loading" sx={{ mt: 3 }}>
              <Skeleton variant="rectangular" height={48} />
              <Skeleton variant="rectangular" height={48} />
              <Skeleton variant="rectangular" height={48} />
            </Box>
          ) : null}
          {isError ? <Alert severity="error" data-testid="assigned-risk-names-addresses-error">{namesMessages.LOAD_ERROR}</Alert> : null}
          {!isLoading && !isError ? (
            <TableContainer sx={(theme) => ({ mt: 3, overflowX: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, transition: 'margin-bottom 0.2s ease', ...scrollbarStyles(theme) })}>
              <Table aria-label="Assigned risk application names and addresses" data-testid="assigned-risk-names-addresses-table">
                <TableHead><TableRow><TableCell colSpan={2}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{namesMessages.NAME_HEADER}<ArrowDownwardIcon fontSize="small" /></Box></TableCell></TableRow></TableHead>
                <TableBody>
                  {namesAndAddresses.length > 0 ? namesAndAddresses.map((row, rowIndex) => (
                    <Row key={`${row.name ?? ''}-${rowIndex}`} row={row} rowIndex={rowIndex} />
                  )) : <TableNoData notFound data-testid="assigned-risk-names-addresses-no-data" />}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
